import React, { useState, useMemo } from 'react';

const RadarChart = ({ data, totalCount }) => { 
  const [tooltip, setTooltip] = useState(null);
  const [useLogScale, setUseLogScale] = useState(false);

  // 1. Augmented data to include "Other" category
  const chartData = useMemo(() => {
    if (!data) return [];
    const currentSum = data.reduce((acc, d) => acc + Number(d.value), 0);
    const otherValue = Math.max(0, Number(totalCount || 0) - currentSum);
    return [...data, { subject: 'Other', value: otherValue }];
  }, [data, totalCount]);

  if (!chartData || chartData.length === 0) return null;

  // Configuration
  const size = 300;
  const center = size / 2;
  const radius = size * 0.4;
  const gridLevels = 3;

  // Use chartData length for the steps
  const angleStep = (Math.PI * 2) / chartData.length;

  // Transform function (linear or log)
  const transform = (v) => {
    if (!useLogScale) return v;
    return Math.log10(Number(v) + 1); // safe for 0 and BigInt
  };

  // Precompute transformed values for chartData
  const transformedValues = useMemo(
    () => chartData.map(d => transform(d.value)),
    [chartData, useLogScale]
  );

  const maxTransformed = Math.max(...transformedValues) || 1;

  const getCoordinates = (value, index) => {
    const transformed = transform(value);
    const transformedTotal = transform(totalCount);

    const r = (transformed / transformedTotal) * radius;
    const theta = index * angleStep - Math.PI / 2;

    return {
      x: center + r * Math.cos(theta),
      y: center + r * Math.sin(theta),
    };
  };

  // Data polygon using chartData
  const dataPoints = chartData.map((d, i) => {
    const { x, y } = getCoordinates(d.value, i);
    return `${x},${y}`;
  }).join(' ');

  // Grid (matches current scale)
  const grids = Array.from({ length: gridLevels }).map((_, levelIndex) => {
    const t = (levelIndex + 1) / gridLevels;
    const scaledValue = t * maxTransformed;

    const r = (scaledValue / maxTransformed) * radius;

    return chartData.map((_, i) => {
      const theta = i * angleStep - Math.PI / 2;
      return `${center + r * Math.cos(theta)},${center + r * Math.sin(theta)}`;
    }).join(' ');
  });

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center font-sans">
      
      {/* Toggle */}
      <div className="absolute -top-4 -left-4 flex gap-2 text-xs">
        <span className="text-black font-bold mt-1 ml-4">Scale:</span>
        <button
          onClick={() => setUseLogScale(false)}
          className={`px-2 py-1 rounded ${!useLogScale ? 'bg-blue-200' : 'bg-gray-200'}`}
        >
          Linear
        </button>
        <button
          onClick={() => setUseLogScale(true)}
          className={`px-2 py-1 rounded ${useLogScale ? 'bg-blue-200' : 'bg-gray-200'}`}
        >
          Log
        </button>
      </div>

      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full max-h-full overflow-visible">

        {/* Grids */}
        {grids.map((pts, i) => (
          <polygon key={`grid-${i}`} points={pts} fill="none" stroke="#e5e7eb" strokeWidth="1" />
        ))}

        {/* Axes + Labels */}
        {chartData.map((d, i) => {
        const theta = i * angleStep - Math.PI / 2;

        const edgeX = center + radius * Math.cos(theta);

        const edgeY = center + radius * Math.sin(theta);

          const labelRadius = radius * 1.15;


          const labelX = center + labelRadius * Math.cos(theta);
          const labelY = center + labelRadius * Math.sin(theta);

          let textAnchor = "middle";
          if (Math.cos(theta) > 0.1) textAnchor = "start";
          else if (Math.cos(theta) < -0.1) textAnchor = "end";

          return (
            <g key={`axis-${i}`}>
              <line x1={center} y1={center} x2={edgeX} y2={edgeY} stroke="#e5e7eb" />

              <text
                x={labelX}
                y={labelY}
                textAnchor={textAnchor}
                alignmentBaseline="middle"
                fontSize="9"
                className="select-none cursor-pointer fill-gray-600 hover:fill-blue-600 hover:font-bold transition-colors duration-200"
                onMouseEnter={(e) => {
                    const rect = e.target.getBoundingClientRect();
                    setTooltip({
                    subject: d.subject,
                    value: d.value,
                    total: totalCount,
                    x: rect.left + rect.width / 2,
                    y: rect.top
                    });
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                {d.subject}
              </text>
            </g>
          );
        })}

        {/* Data Polygon */}
        <polygon
          points={dataPoints}
          fill="#60a5fa"
          fillOpacity="0.5"
          stroke="#3b82f6"
          strokeWidth="2"
        />

        {/* Points */}
        {chartData.map((d, i) => {
          const { x, y } = getCoordinates(d.value, i);
          return (
            <circle
              key={`point-${i}`}
              cx={x}
              cy={y}
              r="2"
              fill="#2563eb"
            />
          );
        })}
      </svg>

      {/* Tooltip */}
     {tooltip && (
    <div
      className="fixed pointer-events-none bg-white text-xs py-1.5 px-3 rounded-xl shadow-md border-2 border-gray-400 whitespace-nowrap z-50 transform -translate-x-1/2 -translate-y-full mb-2"
      style={{ left: tooltip.x, top: tooltip.y - 4 }}
    >
      <div className="font-bold text-gray-800">{tooltip.subject}</div>
      <div className="font-semibold text-gray-800">
        Count:{" "}
        <span className="font-normal text-gray-600">
          {new Intl.NumberFormat('en-US').format(tooltip.value)}
          {tooltip.total > 0 && `/${new Intl.NumberFormat('en-US').format(tooltip.total)}`}
        </span>
      </div>
      {tooltip.total > 0 && (
        <div className="font-semibold text-gray-800">
          Fraction:{" "}
          <span className="font-normal text-gray-600">
            {((Number(tooltip.value) / Number(tooltip.total)) * 100).toFixed(3)} %
          </span>
        </div>
      )}
    </div>
  )}
    </div>
  );
};

export default RadarChart;